import { useState } from 'react'
import { motion } from 'framer-motion'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import {
    User, Mail, Phone, Calendar, MapPin, BookOpen, Target, Award,
    Edit2, Camera, Save, X, Shield, Bell, Moon, Sun
} from 'lucide-react'

// Mock user data
const userData = {
    name: 'Arun Student',
    email: 'arun.student@email.com',
    phone: '+91 98765 43210',
    dob: '2006-05-15',
    address: 'Chennai, Tamil Nadu',
    enrollmentDate: 'Aug 15, 2024',
    batchName: 'NEET 2025 Premium',
    targetExam: 'NEET 2025',
    targetScore: 680,
    avatar: 'AS',
    parentName: 'Mr. Sharma',
    parentPhone: '+91 98765 43211',
    school: 'Delhi Public School',
    class: '12th Science'
}

const stats = {
    testsCompleted: 12,
    avgScore: 645,
    currentRank: 4,
    streak: 15,
    hoursStudied: 486,
}

export function ProfileView() {
    const [isEditing, setIsEditing] = useState(false)
    const [darkMode, setDarkMode] = useState(document.documentElement.classList.contains('dark'))
    const [notifications, setNotifications] = useState(true)

    const toggleDarkMode = () => {
        document.documentElement.classList.toggle('dark')
        setDarkMode(!darkMode)
    }

    return (
        <div className="space-y-4 md:space-y-6 pb-24 lg:pb-0">
            {/* Profile Header */}
            <GlassmorphicCard className="p-6 md:p-8 relative overflow-hidden">
                <div className="absolute -top-20 -right-20 w-60 h-60 rounded-full bg-gradient-to-br from-primary-blue/20 to-accent-gold/10 blur-3xl" />

                <div className="relative flex flex-col md:flex-row md:items-center gap-6">
                    {/* Avatar */}
                    <div className="relative mx-auto md:mx-0">
                        <div className="w-24 h-24 md:w-28 md:h-28 rounded-full bg-gradient-to-br from-primary-blue to-accent-gold flex items-center justify-center text-white font-bold text-3xl shadow-xl">
                            {userData.avatar}
                        </div>
                        <button className="absolute bottom-0 right-0 w-8 h-8 bg-white dark:bg-slate-800 rounded-full shadow-lg flex items-center justify-center hover:scale-110 transition-transform">
                            <Camera className="h-4 w-4 text-slate-600 dark:text-slate-300" />
                        </button>
                    </div>

                    {/* User Info */}
                    <div className="flex-1 text-center md:text-left">
                        <h1 className="text-2xl md:text-3xl font-bold text-theme-primary">{userData.name}</h1>
                        <p className="text-theme-secondary">{userData.batchName}</p>
                        <div className="flex flex-wrap justify-center md:justify-start gap-4 mt-3">
                            <span className="px-3 py-1 bg-primary-blue/10 text-primary-blue text-sm font-medium rounded-full">
                                {userData.targetExam}
                            </span>
                            <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-600 text-sm font-medium rounded-full flex items-center gap-1">
                                <Target className="h-3.5 w-3.5" />
                                Target: {userData.targetScore}
                            </span>
                        </div>
                    </div>

                    {/* Edit Button */}
                    <button
                        onClick={() => setIsEditing(!isEditing)}
                        className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-colors ${isEditing
                            ? 'bg-red-100 text-red-600 hover:bg-red-200'
                            : 'bg-primary-blue/10 text-primary-blue hover:bg-primary-blue hover:text-white'
                            }`}
                    >
                        {isEditing ? <X className="h-4 w-4" /> : <Edit2 className="h-4 w-4" />}
                        {isEditing ? 'Cancel' : 'Edit Profile'}
                    </button>
                </div>
            </GlassmorphicCard>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[
                    { label: 'Tests', value: stats.testsCompleted, icon: BookOpen },
                    { label: 'Avg Score', value: stats.avgScore, icon: Award },
                    { label: 'Rank', value: `#${stats.currentRank}`, icon: Target },
                    { label: 'Streak', value: `${stats.streak}d`, icon: Calendar },
                    { label: 'Hours', value: stats.hoursStudied, icon: User },
                ].map((stat, i) => (
                    <motion.div
                        key={stat.label}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                    >
                        <GlassmorphicCard className="p-4 text-center">
                            <stat.icon className="h-5 w-5 text-primary-blue mx-auto mb-2" />
                            <p className="text-xl font-bold text-theme-primary">{stat.value}</p>
                            <p className="text-xs text-theme-secondary">{stat.label}</p>
                        </GlassmorphicCard>
                    </motion.div>
                ))}
            </div>

            {/* Personal Information */}
            <GlassmorphicCard className="p-4 md:p-6">
                <h3 className="font-bold text-theme-primary mb-4 flex items-center gap-2">
                    <User className="h-5 w-5 text-primary-blue" />
                    Personal Information
                </h3>
                <div className="space-y-4">
                    {[
                        { icon: Mail, label: 'Email', value: userData.email },
                        { icon: Phone, label: 'Phone', value: userData.phone },
                        { icon: Calendar, label: 'Date of Birth', value: new Date(userData.dob).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) },
                        { icon: MapPin, label: 'Location', value: userData.address },
                    ].map((item) => (
                        <div key={item.label} className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                                <item.icon className="h-4 w-4 text-theme-secondary" />
                            </div>
                            <div className="flex-1">
                                <p className="text-xs text-theme-secondary">{item.label}</p>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        defaultValue={item.value}
                                        className="w-full px-2 py-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-sm text-theme-primary"
                                    />
                                ) : (
                                    <p className="font-medium text-theme-primary text-sm">{item.value}</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </GlassmorphicCard>

            {/* Settings */}
            <GlassmorphicCard className="p-4 md:p-6">
                <h3 className="font-bold text-theme-primary mb-4 flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary-blue" />
                    Settings
                </h3>
                <div className="space-y-4">
                    {/* Dark Mode Toggle */}
                    <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                        <div className="flex items-center gap-3">
                            {darkMode ? <Moon className="h-5 w-5 text-primary-blue" /> : <Sun className="h-5 w-5 text-amber-500" />}
                            <div>
                                <p className="font-medium text-theme-primary text-sm">Dark Mode</p>
                                <p className="text-xs text-theme-secondary">Toggle dark/light theme</p>
                            </div>
                        </div>
                        <button
                            onClick={toggleDarkMode}
                            className={`w-12 h-6 rounded-full transition-colors relative ${darkMode ? 'bg-primary-blue' : 'bg-slate-300'
                                }`}
                        >
                            <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${darkMode ? 'translate-x-6' : 'translate-x-0.5'
                                }`} />
                        </button>
                    </div>

                    {/* Notifications Toggle */}
                    <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
                        <div className="flex items-center gap-3">
                            <Bell className="h-5 w-5 text-primary-blue" />
                            <div>
                                <p className="font-medium text-theme-primary text-sm">Notifications</p>
                                <p className="text-xs text-theme-secondary">Receive push notifications</p>
                            </div>
                        </div>
                        <button
                            onClick={() => setNotifications(!notifications)}
                            className={`w-12 h-6 rounded-full transition-colors relative ${notifications ? 'bg-primary-blue' : 'bg-slate-300'
                                }`}
                        >
                            <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${notifications ? 'translate-x-6' : 'translate-x-0.5'
                                }`} />
                        </button>
                    </div>
                </div>
            </GlassmorphicCard>

            {/* Save Button when editing */}
            {isEditing && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="fixed bottom-20 lg:bottom-6 left-4 right-4 lg:left-auto lg:right-6 lg:w-auto z-40"
                >
                    <button className="w-full lg:w-auto px-6 py-3 bg-primary-blue text-white rounded-xl font-medium shadow-lg shadow-primary-blue/30 hover:bg-blue-600 transition-colors flex items-center justify-center gap-2">
                        <Save className="h-5 w-5" />
                        Save Changes
                    </button>
                </motion.div>
            )}
        </div>
    )
}
